import { createApi } from '@reduxjs/toolkit/query/react'
import { ENDPOINTS } from '@constants/index'
import {
  ClaimRecord,
  NullifyArgs,
  UrlResult,
  SubmitClaimArgs,
  CreateClaimRecordPayload,
  NullifyResponse,
  EligibleProductsResult,
  DeleteArgs,
  GetActiveDataArgs,
  ApplyInsuranceArgs,
  ActiveDataResult,
} from '@typesApp/ClaimRecord'
import { GetQuoteArgs } from '@typesApp/QuoteData'
import { ClaimLog, ClaimLogsArgs } from '@typesApp/MyClaim'
import { FileUrlArgs, FileUrlResult, UploadFileArgs } from '@typesApp/PrescriptionUpload'
import { PrescriptionFileArgs } from '@typesApp/PrescriptionFile'
import { ProductSelectionArgs } from '@typesApp/ProductSelection'
import { SubmitResponse } from '@typesApp/SubmitResponse'
import { UpdateProductArgs } from '@typesApp/CustomerApi'
import { ProductBundle, ProductType, ProductTypesEligibility } from '@typesApp/Product'
import { RestrictedCheckArgs, RestrictedCheckStatus } from '@typesApp/Customer'
import { InsurancePlan } from '@typesApp/InsurancePlan'
import { ProductQuoteRequestPayload, ProductQuoteResult } from '@typesApp/ProductQuote'
import { ChangeInsuranceStatusRequest } from '@typesApp/InsuranceMember'
import { dbBaseQueryWithReauth } from './baseQuery'

export const claimRecordsApi = createApi({
  reducerPath: 'claimRecordsApi',
  baseQuery: dbBaseQueryWithReauth,
  tagTypes: ['ActiveClaimRecord', 'ClaimRecord', 'ClaimLog', 'BenefitPlans'],
  endpoints: builder => ({
    createClaimRecord: builder.mutation<ClaimRecord, CreateClaimRecordPayload>({
      query: body => ({
        url: ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS,
        method: 'PUT',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
      invalidatesTags: [{ type: 'ClaimRecord' }],
    }),

    getClaimRecord: builder.query<ClaimRecord, string>({
      query: id => `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${id}`,
      providesTags: [{ type: 'ClaimRecord' }],
    }),

    getClaimRecordNoCache: builder.query<ClaimRecord, string>({
      query: id => `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${id}`,
    }),

    getActiveClaimRecord: builder.query<ActiveDataResult, GetActiveDataArgs>({
      query: ({ customerNumber }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/active/${customerNumber}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'ActiveClaimRecord' }],
    }),

    updateClaimRecord: builder.mutation<ClaimRecord, Partial<ClaimRecord>>({
      query: ({ id, ...patch }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: [{ type: 'ClaimRecord' }],
    }),

    updateRefreshCartTotalFlag: builder.mutation<ClaimRecord, string>({
      query: id => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${id}`,
        method: 'PATCH',
        body: {
          claimData: {
            refreshCartTotalRequired: false,
          },
        },
      }),
      invalidatesTags: [],
    }),

    checkEligibleProducts: builder.mutation<EligibleProductsResult, ProductBundle[]>({
      query: eligibleProducts => ({
        url: `${ENDPOINTS.DIRECT_BILLING.BASE}/check-insurance-eligibility`,
        method: 'POST',
        body: {
          eligibleProducts,
        },
      }),
    }),

    checkEligibleProductTypes: builder.mutation<ProductTypesEligibility, ProductType[]>({
      query: productTypes => ({
        url: `${ENDPOINTS.DIRECT_BILLING.BASE}/check-product-types-eligibility`,
        method: 'POST',
        body: {
          productTypes,
        },
      }),
    }),

    deleteClaimRecord: builder.mutation<void, DeleteArgs>({
      query: args => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${args.claimRecordId}`,
        method: 'DELETE',
        body: {
          id: args.claimRecordId,
          cartId: args.cartId,
          customerNumber: args.customerNumber,
          customerEmail: args.customerEmail,
        },
      }),
    }),

    generateUploadUrl: builder.mutation<FileUrlResult, FileUrlArgs>({
      query: ({ claimRecordId, fileName }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/prescriptions/put-url?file-name=${fileName}`,
        method: 'POST',
      }),
    }),

    uploadFile: builder.mutation<void, UploadFileArgs>({
      query: ({ url, claimId, fileName, content }) => ({
        url: url,
        method: 'PUT',
        headers: {
          ['x-amz-meta-claim-record-id']: claimId,
          ['x-amz-meta-file-name']: fileName,
        },
        body: content,
      }),
      invalidatesTags: [{ type: 'ClaimRecord' }],
    }),

    validatePrescription: builder.mutation<ClaimRecord, string>({
      query: claimRecordId => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/validate`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'ClaimRecord' }],
    }),

    generateDownloadUrl: builder.mutation<UrlResult, PrescriptionFileArgs>({
      query: ({ claimRecordId, prescriptionId }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/prescriptions/${prescriptionId}/get-url`,
        method: 'POST',
      }),
      invalidatesTags: [],
    }),

    getPrescriptionFileBlob: builder.query<string, string>({
      query: url => ({
        url: url,
        /* serialize blob into url */
        responseHandler: response => response.blob().then(blob => URL.createObjectURL(blob)),
      }),
      providesTags: [],
    }),

    deletePrescriptionFile: builder.mutation<void, PrescriptionFileArgs>({
      query: ({ claimRecordId, prescriptionId }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/prescriptions/${prescriptionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ClaimRecord' }],
    }),

    selectClaimableProducts: builder.mutation<void, ProductSelectionArgs>({
      query: ({ claimRecordId, prescriptionId, fundedBenefitProductIds, additionalPurchaseProductIds }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/products/select-claimable`,
        method: 'POST',
        body: {
          claimRecordId,
          prescriptionId,
          fundedBenefitProductIds,
          additionalPurchaseProductIds,
        },
      }),
      invalidatesTags: [{ type: 'ClaimRecord' }],
    }),

    submitClaim: builder.mutation<SubmitResponse, SubmitClaimArgs>({
      query: (args: SubmitClaimArgs) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${args.claimRecordId}/claim/submit`,
        method: 'POST',
        body: args,
      }),
      transformResponse: (response: SubmitResponse, meta: { response: { status: number } }) => {
        return {
          ...response,
          httpStatus: meta?.response.status,
        }
      },
      extraOptions: {
        maxRetries: 0,
      },
      // no need to invalidate ClaimRecord because submit will refetch as required
      invalidatesTags: [{ type: 'ClaimLog' }],
    }),

    nullifyClaim: builder.mutation<NullifyResponse, NullifyArgs>({
      query: ({ claimRecordId, reason, initiatedBy }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/claim/nullify`,
        method: 'POST',
        body: {
          claimRecordId,
          reason,
          initiatedBy,
        },
      }),
      extraOptions: {
        maxRetries: 0,
      },
      // no need to invalidate ClaimRecord because nullify will refetch as required
      invalidatesTags: [],
    }),

    restrictedCheck: builder.query<RestrictedCheckStatus, RestrictedCheckArgs>({
      query: ({ customerNumber, ecommerce }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/customer/${customerNumber}/restricted-check`,
        params: { ecommerce },
        method: 'POST',
      }),
    }),

    getClaimLogs: builder.query<ClaimLog[], ClaimLogsArgs>({
      query: ({ customerId, legacyCustomerId }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIMS}/customer/${customerId}/claims${legacyCustomerId ? '?legacyCustomerNumber=' + legacyCustomerId : ''}`,
        method: 'GET',
        providesTags: [{ type: 'ClaimLog' }],
      }),
      transformResponse: (response: { claims: ClaimLog[] }) => response.claims,
    }),

    getQuote: builder.mutation<SubmitResponse, GetQuoteArgs>({
      query: args => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${args.claimRecordId}/quote/request-new`,
        method: 'POST',
        body: args,
      }),
      transformResponse: (response: SubmitResponse, meta: { response: { status: number } }) => {
        return {
          ...response,
          httpStatus: meta?.response.status,
        }
      },
      extraOptions: {
        maxRetries: 0,
      },
      invalidatesTags: ['ClaimRecord'],
    }),

    acceptQuote: builder.mutation<SubmitResponse, string>({
      query: (claimRecordId: string) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/quote/cx-accept`,
        method: 'POST',
      }),
      transformResponse: (response: SubmitResponse, meta: { response: { status: number } }) => {
        return {
          ...response,
          httpStatus: meta?.response.status,
        }
      },
      extraOptions: {
        maxRetries: 0,
      },
      invalidatesTags: ['ClaimRecord'],
    }),

    refuseQuote: builder.mutation<SubmitResponse, string>({
      query: (claimRecordId: string) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/quote/cx-refuse`,
        method: 'POST',
      }),
      transformResponse: (response: SubmitResponse, meta: { response: { status: number } }) => {
        return {
          ...response,
          httpStatus: meta?.response.status,
        }
      },
      extraOptions: {
        maxRetries: 0,
      },
      invalidatesTags: ['ClaimRecord'],
    }),

    applyInsurance: builder.mutation<ClaimRecord, ApplyInsuranceArgs>({
      query: ({ claimRecordId, payload }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/sync-insurance-member`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: {
        maxRetries: 0,
      },
      invalidatesTags: ['ClaimRecord', 'BenefitPlans'],
    }),

    insuranceMemberEligibilityCheck: builder.mutation<ClaimRecord, string>({
      query: (claimRecordId: string) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/insurance-member-eligibility-check`,
        method: 'POST',
      }),
      invalidatesTags: ['ClaimRecord', 'BenefitPlans'],
    }),

    updateProducts: builder.mutation<void, UpdateProductArgs>({
      query: args => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CUSTOMER}/${args?.customerNumber}/products`,
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['ClaimRecord'],
    }),

    changeInsuranceStatus: builder.mutation<ClaimRecord, ChangeInsuranceStatusRequest>({
      query: ({ claimRecordId, useInsuranceStatus }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/change-insurance-status`,
        method: 'POST',
        body: {
          useInsuranceStatus,
        },
      }),
      extraOptions: {
        maxRetries: 0,
      },
      invalidatesTags: ['ClaimRecord'],
    }),

    getInsurancePlans: builder.query<InsurancePlan[], { claimRecordId: string }>({
      query: ({ claimRecordId }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/insurance-plans`,
        method: 'GET',
      }),
      transformResponse: (response: { insurancePlans: InsurancePlan[] }) => response.insurancePlans,
      providesTags: ['BenefitPlans'],
    }),

    getProductQuote: builder.query<ProductQuoteResult, ProductQuoteRequestPayload>({
      query: ({ claimRecordId, products }) => ({
        url: `${ENDPOINTS.DIRECT_BILLING.CLAIM_RECORDS}/${claimRecordId}/quote/request-new-product-quotes`,
        method: 'POST',
        body: {
          products,
        },
      }),
    }),
  }),
})

export const {
  useCreateClaimRecordMutation,
  useGetClaimRecordQuery,
  useLazyGetClaimRecordQuery,
  useLazyGetClaimRecordNoCacheQuery,
  useGetActiveClaimRecordQuery,
  useLazyGetActiveClaimRecordQuery,
  useUpdateClaimRecordMutation,
  useUpdateRefreshCartTotalFlagMutation,
  useCheckEligibleProductsMutation,
  useCheckEligibleProductTypesMutation,
  useDeleteClaimRecordMutation,
  useGenerateUploadUrlMutation,
  useUploadFileMutation,
  useValidatePrescriptionMutation,
  useGenerateDownloadUrlMutation,
  useGetPrescriptionFileBlobQuery,
  useDeletePrescriptionFileMutation,
  useSelectClaimableProductsMutation,
  useSubmitClaimMutation,
  useNullifyClaimMutation,
  useRestrictedCheckQuery,
  useLazyRestrictedCheckQuery,
  usePrefetch: useClaimRecordPrefetch,
  useGetClaimLogsQuery,
  useGetQuoteMutation,
  useAcceptQuoteMutation,
  useRefuseQuoteMutation,
  useApplyInsuranceMutation,
  useUpdateProductsMutation,
  useInsuranceMemberEligibilityCheckMutation,
  useChangeInsuranceStatusMutation,
  useGetInsurancePlansQuery,
  useLazyGetInsurancePlansQuery,
  useLazyGetProductQuoteQuery,
} = claimRecordsApi
